import {ActionList, ActionMenu, Button, IconButton, Label, Tooltip} from '@primer/react'
import {AlertIcon, CloudIcon, DeviceDesktopIcon, PlayIcon, SlidersIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useMemo, useState} from 'react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {clsx} from 'clsx'

import {CLOUD_ENVIRONMENT_OPTION, SANDBOX_ENVIRONMENT_OPTION, type EnvironmentOption} from './environment-option'
import {buildAhpOptions} from './ahp-options'
import {useEnvironments} from '@github-ui/copilot-shared-ui/environments/use-environments'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {ENVIRONMENT_STATUS_LABELS} from '@github-ui/copilot-shared-ui/environments/environment-display'
import styles from './EnvironmentPicker.module.css'

/** Trailing label shown next to an online environment whose AHP protocol
 * version this client cannot speak. */
const UNSUPPORTED_VERSION_LABEL = 'Unsupported'

/** The trailing label explaining why a disabled AHP option can't be selected:
 * an incompatible protocol version takes precedence over a non-online status. */
const disabledEnvironmentLabel = (env: EnvironmentOption): string | undefined => {
  if (!env.disabled) return undefined
  if (env.status === 'online' && env.versionSupported === false) return UNSUPPORTED_VERSION_LABEL
  return env.status ? ENVIRONMENT_STATUS_LABELS[env.status] : undefined
}

type EnvironmentPickerProps = {
  /** Available environments to choose from. Grouped in the dropdown by `kind`.
   * If omitted, defaults to the built-in cloud environments plus the live list
   * of AHP environments fetched via `useEnvironments`. */
  environments?: EnvironmentOption[]
  selectedEnvironmentId?: string
  onSelectEnvironment?: (environment: EnvironmentOption) => void
  /** Visual treatment of the trigger button. `toolbar` (default) matches the
   * medium, bordered selectors in the chat input toolbar; `strip` matches the
   * compact pills inside the environment strip. */
  appearance?: 'toolbar' | 'strip'
  disabled?: boolean
  /** Adds the managed-sandbox (`github-sandbox`) top-line option above Actions.
   * Only enable on surfaces whose submit handler drives the environment relay. */
  includeSandboxOption?: boolean
  /** Enables the picker-owned announcement for a controlled unavailable selection. */
  announceUnavailable?: boolean
  /** When true, always show only the icon button regardless of viewport size. */
  iconOnly?: boolean
  /** Optional class for scoping trigger styling to a composer surface. */
  triggerClassName?: string
  design?: 'default' | 'simplified'
  isChatSelected?: boolean
  /** Effective selection, including a Sandbox selection held outside the available option list. */
  selectedOption?: EnvironmentOption | null
}

export function EnvironmentPicker({
  environments,
  selectedEnvironmentId,
  onSelectEnvironment,
  appearance = 'toolbar',
  disabled = false,
  includeSandboxOption = false,
  announceUnavailable = true,
  iconOnly = false,
  triggerClassName,
  design = 'default',
  isChatSelected = false,
  selectedOption: effectiveSelectedOption,
}: EnvironmentPickerProps) {
  const preferredOverlaySide = useOverlayDirection()
  const tooltipDirection =
    preferredOverlaySide === 'outside-top' || preferredOverlaySide === 'inside-top' ? 'n' : undefined
  const {data: ahpEnvironments, refetch} = useEnvironments()
  // Collapse earlier to keep the task toolbar on one row in medium widths.
  const isNarrowViewport = useMediaQuery('(max-width: calc(80rem - 0.02px))')
  const showIconOnly = iconOnly || isNarrowViewport

  const allEnvironments = useMemo<EnvironmentOption[]>(() => {
    if (environments) return environments
    // Actions stays first here because this list also determines the default selection.
    const defaults = includeSandboxOption
      ? [CLOUD_ENVIRONMENT_OPTION, SANDBOX_ENVIRONMENT_OPTION]
      : [CLOUD_ENVIRONMENT_OPTION]
    return [...defaults, ...buildAhpOptions(ahpEnvironments ?? [])]
  }, [environments, ahpEnvironments, includeSandboxOption])

  // Keep display order separate from the list used for default selection.
  const topLevelEnvironments = useMemo(
    () => [
      ...allEnvironments.filter(env => env.kind === 'sandbox'),
      ...allEnvironments.filter(env => env.kind === 'cloud'),
    ],
    [allEnvironments],
  )
  const ahpOptions = useMemo(
    () =>
      allEnvironments
        .filter(env => env.kind === 'ahp')
        // Surface selectable (online + compatible) environments first, keeping
        // the unavailable ones grouped below in their original order.
        .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled)),
    [allEnvironments],
  )

  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    () => selectedEnvironmentId ?? allEnvironments[0]?.id ?? CLOUD_ENVIRONMENT_OPTION.id,
  )
  const requestedId = selectedEnvironmentId ?? internalSelectedId
  const requestedOption = allEnvironments.find(env => env.id === requestedId)
  // A disabled option can't be the effective selection: once the owner clears an
  // unavailable selection, the retained internal id may still point at it, so
  // fall back to the default (first selectable) option for display and
  // highlighting instead of showing a now-disabled environment as active.
  const selected =
    requestedOption && !requestedOption.disabled
      ? requestedOption
      : (allEnvironments.find(env => !env.disabled) ?? allEnvironments[0])
  const activeId = selected?.id

  // The selected environment can stop being a valid target while it stays the
  // controlled selection — it dropped out of the list, or it is still listed but
  // is no longer selectable (e.g. an AHP environment went offline). The owner of
  // the selection is responsible for resetting it; here we only surface the
  // change to assistive tech via the live region below.
  const listedSelectedOption = selectedEnvironmentId
    ? allEnvironments.find(env => env.id === selectedEnvironmentId)
    : undefined
  const isSelectedUnavailable =
    announceUnavailable && !!selectedEnvironmentId && (!listedSelectedOption || !!listedSelectedOption.disabled)

  const handleSelect = (env: EnvironmentOption) => {
    setInternalSelectedId(env.id)
    onSelectEnvironment?.(env)
  }

  const isSimplified = design === 'simplified'
  const displayedSelection = isSimplified ? (effectiveSelectedOption ?? selected) : selected
  const TriggerIcon = selected?.icon ?? CLOUD_ENVIRONMENT_OPTION.icon
  const triggerLabel =
    displayedSelection?.kind === 'sandbox'
      ? isSimplified
        ? 'Cloud'
        : displayedSelection.label
      : (displayedSelection?.label ?? CLOUD_ENVIRONMENT_OPTION.label)
  const buttonClassName = clsx(
    appearance === 'strip' ? styles.selectorButton : styles.toolbarButton,
    !showIconOnly && triggerClassName,
  )
  const trigger = isSimplified ? (
    <IconButton
      icon={SlidersIcon}
      aria-label={`Environment: ${triggerLabel}`}
      tooltipDirection={tooltipDirection}
      disabled={disabled}
      variant="invisible"
      size={appearance === 'strip' ? 'small' : 'medium'}
      className={buttonClassName}
    />
  ) : showIconOnly ? (
    <IconButton
      icon={TriggerIcon}
      aria-label={`Environment: ${triggerLabel}`}
      disabled={disabled}
      variant="invisible"
      size={appearance === 'strip' ? 'small' : 'medium'}
      className={buttonClassName}
    />
  ) : (
    <Button
      leadingVisual={TriggerIcon}
      trailingAction={TriangleDownIcon}
      aria-label={`Environment: ${triggerLabel}`}
      disabled={disabled}
      variant="invisible"
      size={appearance === 'strip' ? 'small' : 'medium'}
      className={buttonClassName}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <>
      <ActionMenu
        onOpenChange={open => {
          if (open) refetch()
        }}
      >
        <ActionMenu.Anchor>
          {disabled || isSimplified ? (
            trigger
          ) : (
            <Tooltip text="Environment" direction={tooltipDirection}>
              {trigger}
            </Tooltip>
          )}
        </ActionMenu.Anchor>
        <ActionMenu.Overlay
          width={isSimplified ? 'small' : 'auto'}
          overflow="auto"
          className={isSimplified ? styles.simplifiedMenu : undefined}
        >
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Environment</ActionList.GroupHeading>
              {isSimplified ? (
                <>
                  {topLevelEnvironments
                    .filter(env => env.kind === 'sandbox')
                    .map(env => (
                      <ActionList.Item
                        key={env.id}
                        selected={env.id === (effectiveSelectedOption?.id ?? activeId)}
                        onSelect={() => handleSelect(env)}
                      >
                        <ActionList.LeadingVisual>
                          <CloudIcon />
                        </ActionList.LeadingVisual>
                        <span className={styles.simplifiedOptionLabel}>
                          Cloud <Label variant="accent">NEW</Label>
                        </span>
                      </ActionList.Item>
                    ))}
                  {topLevelEnvironments
                    .filter(env => env.kind === 'cloud')
                    .map(env => (
                      <ActionList.Item
                        key={env.id}
                        role={isChatSelected ? 'menuitem' : undefined}
                        selected={env.id === (effectiveSelectedOption?.id ?? activeId)}
                        inactiveText={isChatSelected ? "Chats can't be run on Actions" : undefined}
                        onSelect={() => handleSelect(env)}
                      >
                        <ActionList.LeadingVisual>
                          <PlayIcon />
                        </ActionList.LeadingVisual>
                        Actions
                        {isChatSelected && (
                          <ActionList.TrailingVisual>
                            <AlertIcon />
                          </ActionList.TrailingVisual>
                        )}
                      </ActionList.Item>
                    ))}
                  <ActionMenu>
                    <ActionMenu.Anchor>
                      <ActionList.Item role="menuitem">
                        <ActionList.LeadingVisual>
                          <DeviceDesktopIcon />
                        </ActionList.LeadingVisual>
                        Remote
                      </ActionList.Item>
                    </ActionMenu.Anchor>
                    <ActionMenu.Overlay side="outside-right" width="small" overflow="auto">
                      <ActionList selectionVariant="single">
                        <ActionList.Group>
                          {ahpOptions.length > 0 ? (
                            ahpOptions.map(env => {
                              const unavailableLabel = disabledEnvironmentLabel(env)
                              return (
                                <ActionList.Item
                                  key={env.id}
                                  selected={env.id === (effectiveSelectedOption?.id ?? activeId)}
                                  disabled={env.disabled}
                                  onSelect={() => handleSelect(env)}
                                >
                                  <ActionList.LeadingVisual>
                                    <DeviceDesktopIcon />
                                  </ActionList.LeadingVisual>
                                  {env.label}
                                  {unavailableLabel && (
                                    <ActionList.TrailingVisual>
                                      <Label variant="secondary">{unavailableLabel}</Label>
                                    </ActionList.TrailingVisual>
                                  )}
                                </ActionList.Item>
                              )
                            })
                          ) : (
                            <ActionList.Item role="menuitem" disabled>
                              No Mission Control environments available
                            </ActionList.Item>
                          )}
                        </ActionList.Group>
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                </>
              ) : (
                <>
                  {topLevelEnvironments.map(env => (
                    <ActionList.Item key={env.id} selected={env.id === activeId} onSelect={() => handleSelect(env)}>
                      <ActionList.LeadingVisual>
                        <env.icon />
                      </ActionList.LeadingVisual>
                      {env.label}
                    </ActionList.Item>
                  ))}
                  {ahpOptions.map(env => {
                    const unavailableLabel = disabledEnvironmentLabel(env)
                    return (
                      <ActionList.Item
                        key={env.id}
                        selected={env.id === activeId}
                        disabled={env.disabled}
                        onSelect={() => handleSelect(env)}
                      >
                        <ActionList.LeadingVisual>
                          <env.icon />
                        </ActionList.LeadingVisual>
                        {env.label}
                        {unavailableLabel && (
                          <ActionList.TrailingVisual>
                            <Label variant="secondary">{unavailableLabel}</Label>
                          </ActionList.TrailingVisual>
                        )}
                      </ActionList.Item>
                    )
                  })}
                </>
              )}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <div className="sr-only" role="status" aria-live="polite">
        {isSelectedUnavailable
          ? 'Selected environment is no longer available. Switched to the default environment.'
          : ''}
      </div>
    </>
  )
}
